import React from 'react'
import type { IconProps } from '../Icon/types'
import {
  IconAtom,
  IconBoltDefault,
  IconContentFace,
  IconDeskGlobe,
  IconFlowerPicture,
  IconForkSpoon,
  IconHandshake,
  IconHeartHome,
  IconInstitution,
  IconMountainSummit,
  IconMusicNotes,
  IconOpenBook,
  IconPaintPalette,
  IconPencilAndRuler,
  IconPeopleAndCulture,
  IconPeopleConversing,
  IconPersonRunningLeft,
} from '../Icons'

const FALLBACK_ICON = IconDeskGlobe

// Only top-level categories need an icon here — subcategories do not get their
// own icons, so do not add subcategory slugs to this map.
const CATEGORY_ICONS = {
  'health-wellness': IconContentFace,
  'social-change-activism': IconPeopleAndCulture,
  'sports-gaming': IconPersonRunningLeft,
  // content editorial taxonomy
  'food-drink': IconForkSpoon,
  'tv-film-theater': IconFlowerPicture,
  'art-design': IconPencilAndRuler,
  'music-2': IconMusicNotes,
  'writing-2': IconOpenBook,
  'sports-gaming-2': IconPersonRunningLeft,
  'beauty-style': IconPaintPalette,
  'relationships-selfimprovement': IconPeopleConversing,
  'wellness-2': IconContentFace,
  'business-career': IconHandshake,
  'science-math': IconAtom,
  'tech-digital-media': IconBoltDefault,
  'society-government': IconInstitution,
  'acting-performing-arts': IconPeopleConversing,
  'business-entrepreneurship': IconHandshake,
  'community-gov-3': IconInstitution,
  culinary: IconForkSpoon,
  'design-creative-arts': IconPaintPalette,
  'digital-content-gaming': IconBoltDefault,
  'film-television-production': IconFlowerPicture,
  'health-wellness-3': IconContentFace,
  'music-3': IconMusicNotes,
  'outdoor-adventures-events': IconMountainSummit,
  'science-tech-3': IconAtom,
  'sports-athletics': IconPersonRunningLeft,
  'writing-3': IconOpenBook,
  // test taxonomy
  'arts-crafts': IconPencilAndRuler,
  cooking: IconForkSpoon,
  // original categories
  'business-politics-society': IconHandshake, // Business
  'community-government': IconInstitution, // Community & Government
  'culinary-arts': IconForkSpoon, // Food
  'design-photography-fashion': IconPencilAndRuler, // Design & Style
  'film-tv': IconFlowerPicture, // Arts & Entertainment
  lifestyle: IconHeartHome, // Home & Lifestyle
  'music-entertainment': IconMusicNotes, // Music
  'science-technology': IconAtom,
  'sports-games': IconPersonRunningLeft,
  wellness: IconContentFace,
  writing: IconOpenBook,
  // contentful article categories
  food: IconForkSpoon,
  'design-style': IconPaintPalette,
  'arts-entertainment': IconFlowerPicture,
  music: IconMusicNotes,
  business: IconHandshake,
  'science-tech': IconAtom,
  'home-lifestyle': IconHeartHome,
} as const

type Slug = keyof typeof CATEGORY_ICONS

export const isCategorySlug = (slug: string): slug is Slug =>
  slug in CATEGORY_ICONS

type CategoryIconProps = {
  slug: string
  style?: React.CSSProperties
} & IconProps
/**
 * Returns the standard icon given a Category slug.
 *
 * These icons are most likely going to be located beside/near the respective Category name,
 * in which case the `srText` prop should be left empty as the Category name will be read by a screen reader.
 */
export const CategoryIcon = ({ slug, ...props }: CategoryIconProps) => {
  const Icon = isCategorySlug(slug) ? CATEGORY_ICONS[slug] : FALLBACK_ICON

  return <Icon srText='' {...props} />
}
